import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router';
import { Check, ChevronDown } from 'lucide-react';
import { useUiStore } from '../store/ui.js';
import { useUnsavedStore, confirmUnsaved } from '../store/unsaved.js';
import { ALL_ENTITIES_LABEL } from '../lib/entitySwitch.js';
import { useEntities, useEmployees, type EntityDTO } from '../api/hooks.js';

// Sélecteur de société de la topbar : un bouton « Société » qui porte la
// société courante, un menu qui liste Toutes + les sociétés visibles
// avec leur effectif.
//
// La sélection vit à deux endroits que ce composant est seul à
// synchroniser :
//   - useUiStore.currentEntityId, mémorisé par appareil ;
//   - l'URL ?entity=<id>, partageable. Quand elle porte le paramètre,
//     elle gagne ; sinon la valeur mémorisée y est réécrite, ce qui fait
//     survivre la société aux liens du menu latéral.
//
// Le changement passe d'abord par le registre des brouillons
// (confirmUnsaved) puis ouvre un « switch » que useEntitySwitchLanding
// referme quand les données sont là : barre de progression, lueur,
// contenu en retrait puis remontée.

interface Props {
  // Extras de la Saisie : effectifs du jour par société + légende. Sans
  // eux, le menu affiche l'effectif actif de chaque société.
  counts?: Record<string, number>;
  caption?: string;
}

const MENU_CLOSE_MS = 160;
const ROLL_MS = 340;

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() =>
    typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 767px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const on = () => setMobile(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return mobile;
}

export function EntitySelect({ counts, caption }: Props = {}) {
  const { data: entities } = useEntities();
  const { data: employees } = useEmployees();
  const [searchParams, setSearchParams] = useSearchParams();
  const current = useUiStore((s) => s.currentEntityId);
  const setCurrent = useUiStore((s) => s.setCurrentEntityId);
  const flashing = useUiStore((s) => s.entityBarFlashing);
  const switching = useUiStore((s) => s.entitySwitch);
  const beginSwitch = useUiStore((s) => s.beginEntitySwitch);
  const dirty = useUnsavedStore((s) => Object.keys(s.entries).length > 0);
  const mobile = useIsMobile();

  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [hl, setHl] = useState<number | null>(null);
  const [pingSeq, setPingSeq] = useState(0);
  const [washX, setWashX] = useState(70);
  const [phase, setPhase] = useState<'idle' | 'run' | 'done'>('idle');
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);

  const list: EntityDTO[] = useMemo(() => entities ?? [], [entities]);
  const currentEntity = list.find((e) => e.id === current) ?? null;

  // Effectifs : ceux de la Saisie quand elle les fournit, sinon les
  // employés actifs par société.
  const numbers = useMemo(() => {
    if (counts) return counts;
    const m: Record<string, number> = {};
    for (const e of employees ?? []) m[e.entityId] = (m[e.entityId] ?? 0) + 1;
    return m;
  }, [counts, employees]);
  const hasNumbers = !!counts || !!employees;
  const total = Object.values(numbers).reduce((a, b) => a + b, 0);
  const numberFor = (id: string) => (id === 'all' ? total : (numbers[id] ?? 0));

  // ─── URL ⇄ store ─────────────────────────────────────────────
  const urlEntity = searchParams.get('entity');
  useEffect(() => {
    if (urlEntity) {
      if (urlEntity !== current) setCurrent(urlEntity);
      return;
    }
    if (current !== 'all') {
      const params = new URLSearchParams(searchParams);
      params.set('entity', current);
      setSearchParams(params, { replace: true });
    }
  }, [urlEntity, current, setCurrent, searchParams, setSearchParams]);

  // Une société hors de portée (lien reçu, compte restreint) retombe sur
  // « Toutes » plutôt que d'afficher un écran vide.
  useEffect(() => {
    if (!entities || current === 'all' || entities.some((e) => e.id === current)) return;
    setCurrent('all');
    const params = new URLSearchParams(searchParams);
    params.delete('entity');
    setSearchParams(params, { replace: true });
  }, [entities, current, setCurrent, searchParams, setSearchParams]);

  // ─── barre de progression ────────────────────────────────────
  useEffect(() => {
    if (switching) {
      setPhase('run');
      return;
    }
    setPhase((p) => (p === 'run' ? 'done' : p));
  }, [switching]);
  useEffect(() => {
    if (phase !== 'done') return undefined;
    const t = window.setTimeout(() => setPhase('idle'), 500);
    return () => window.clearTimeout(t);
  }, [phase]);

  // ─── nom qui roule ───────────────────────────────────────────
  const displayName = current === 'all'
    ? 'Toutes'
    : (mobile ? (currentEntity?.short ?? current) : (currentEntity?.name ?? current));
  const prevName = useRef(displayName);
  const [leaving, setLeaving] = useState<string | null>(null);
  useEffect(() => {
    if (prevName.current === displayName) return undefined;
    setLeaving(prevName.current);
    prevName.current = displayName;
    const t = window.setTimeout(() => setLeaving(null), ROLL_MS);
    return () => window.clearTimeout(t);
  }, [displayName]);

  // ─── menu ────────────────────────────────────────────────────
  const closeMenu = useCallback(() => {
    if (!open || closing) return;
    setClosing(true);
    closeTimer.current = window.setTimeout(() => {
      setOpen(false);
      setClosing(false);
      setHl(null);
      closeTimer.current = null;
    }, MENU_CLOSE_MS);
  }, [open, closing]);
  useEffect(() => () => { if (closeTimer.current) window.clearTimeout(closeTimer.current); }, []);

  const openMenu = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setClosing(false);
    setOpen(true);
  };

  // Focus l'entrée active à l'ouverture ; ferme au clic hors du menu.
  useEffect(() => {
    if (!open) return undefined;
    const active = menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitemradio"][aria-checked="true"]')
      ?? menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitemradio"]');
    active?.focus();
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) closeMenu();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, closeMenu]);

  const items = useMemo(() => [{ id: 'all' } as { id: string }, ...list], [list]);

  const pick = useCallback(async (id: string) => {
    closeMenu();
    if (id === current || switching) return;
    const ent = list.find((e) => e.id === id);
    const target = id === 'all' ? ALL_ENTITIES_LABEL : (ent?.name ?? id);
    const ok = await confirmUnsaved(target);
    if (!ok) return;
    // Lueur : part du bouton, en pourcentage de la largeur de la topbar.
    const btn = triggerRef.current;
    const bar = btn?.closest<HTMLElement>('.topbar');
    if (btn && bar) {
      const r = btn.getBoundingClientRect();
      const b = bar.getBoundingClientRect();
      if (b.width > 0) setWashX(Math.round(((r.left + r.width / 2 - b.left) / b.width) * 100));
    }
    setPingSeq((s) => s + 1);
    beginSwitch(id);
    setCurrent(id);
    const params = new URLSearchParams(searchParams);
    if (id === 'all') params.delete('entity');
    else params.set('entity', id);
    setSearchParams(params, { replace: true });
    triggerRef.current?.focus();
  }, [closeMenu, current, switching, list, beginSwitch, setCurrent, searchParams, setSearchParams]);

  // Alt+1 = Toutes, Alt+2… = sociétés dans l'ordre du menu.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || !/^Digit[1-9]$/.test(e.code)) return;
      const n = Number(e.code.slice(5));
      const item = items[n - 1];
      if (!item) return;
      e.preventDefault();
      void pick(item.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, pick]);

  const onMenuKey = (e: React.KeyboardEvent) => {
    const nodes = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ?? []);
    const i = nodes.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      nodes[(i + 1) % nodes.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      nodes[(i - 1 + nodes.length) % nodes.length]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      nodes[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      nodes[nodes.length - 1]?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
      triggerRef.current?.focus();
    }
  };

  const colorOf = (id: string) => (id === 'all' ? 'var(--accent)' : (list.find((e) => e.id === id)?.color ?? 'var(--ink)'));
  const swId = switching?.targetId ?? current;
  const swStyle = { '--sw': colorOf(swId) } as CSSProperties;
  const rootClass = [
    'entity-select',
    current !== 'all' ? 'is-tinted' : '',
    switching ? 'is-busy' : '',
    flashing ? 'is-flashing' : '',
    counts ? 'has-counts' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <div className={rootClass} ref={rootRef} style={swStyle} data-testid="entity-bar">
        <button
          type="button"
          ref={triggerRef}
          className="esel-btn"
          aria-haspopup="menu"
          aria-expanded={open && !closing}
          aria-busy={!!switching}
          aria-label={`Société : ${current === 'all' ? ALL_ENTITIES_LABEL : (currentEntity?.name ?? current)}`}
          onClick={() => (open && !closing ? closeMenu() : openMenu())}
          data-testid="entity-select-trigger"
        >
          <span className="esel-lbl" aria-hidden="true">Société</span>
          <span className={current === 'all' ? 'esel-dot is-all' : 'esel-dot'} aria-hidden="true" />
          <span className="esel-roll">
            <span key={displayName} className="esel-name in" data-testid="entity-select-name">{displayName}</span>
            {leaving && <span className="esel-name out" aria-hidden="true">{leaving}</span>}
          </span>
          {hasNumbers && <span className="esel-cnt">{numberFor(current)}</span>}
          <ChevronDown className="esel-chev" size={14} aria-hidden="true" />
          {dirty && <span className="esel-dirty" title="Modifications non enregistrées" data-testid="entity-select-dirty" />}
        </button>
        {pingSeq > 0 && <span key={pingSeq} className="esel-ping" aria-hidden="true" />}

        {open && (
          <div
            className={closing ? 'esel-menu is-closing' : 'esel-menu'}
            role="menu"
            aria-label="Choisir une société"
            ref={menuRef}
            onKeyDown={onMenuKey}
            onMouseLeave={() => setHl(null)}
            data-testid="entity-select-menu"
          >
            <div className="esel-mh">
              <span>Société</span>
              <span className="esel-hint" aria-hidden="true">⌥1…{items.length} · ↑↓ · Entrée</span>
            </div>
            {caption && <div className="esel-caption">{caption}</div>}
            <span
              className="esel-hl"
              aria-hidden="true"
              style={hl == null ? { opacity: 0 } : { opacity: 1, transform: `translateY(${hl}px)` }}
            />
            {items.map((it, i) => {
              const isAll = it.id === 'all';
              const ent = isAll ? null : (it as EntityDTO);
              const active = it.id === current;
              const n = numberFor(it.id);
              const w = total > 0 ? Math.round((n / total) * 100) : 0;
              const style = {
                '--c': isAll ? 'var(--ink)' : ent!.color,
                '--i': i,
                '--w': `${w}%`,
              } as CSSProperties;
              return (
                <button
                  key={it.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  className={active ? 'esel-item is-active' : 'esel-item'}
                  style={style}
                  data-e={isAll ? 'all' : ent!.short.toLowerCase()}
                  data-testid={`entity-pill-${it.id}`}
                  title={isAll ? ALL_ENTITIES_LABEL : ent!.name}
                  onMouseEnter={(e) => setHl(e.currentTarget.offsetTop)}
                  onFocus={(e) => setHl(e.currentTarget.offsetTop)}
                  onClick={() => void pick(it.id)}
                >
                  <span className={isAll ? 'esel-rail is-all' : 'esel-rail'} aria-hidden="true" />
                  <span className="esel-nm">
                    <span>
                      {isAll ? ALL_ENTITIES_LABEL : ent!.name}
                      {!isAll && <small>{ent!.short}</small>}
                    </span>
                    {hasNumbers && (
                      <span className={isAll ? 'esel-bar is-all' : 'esel-bar'} aria-hidden="true"><i /></span>
                    )}
                  </span>
                  {hasNumbers && <span className="esel-n">{n}</span>}
                  <Check className="esel-ck" size={15} aria-hidden="true" />
                </button>
              );
            })}
          </div>
        )}
      </div>
      {/* Hors du sélecteur (position: relative) : ces deux calques se
          posent sur toute la topbar, dans la couleur de la société visée. */}
      <span className="topbar-progress" data-phase={phase} style={swStyle} aria-hidden="true" />
      {pingSeq > 0 && (
        <span
          key={pingSeq}
          className="topbar-wash"
          style={{ ...swStyle, '--wx': `${washX}%` } as CSSProperties}
          aria-hidden="true"
        />
      )}
    </>
  );
}
